/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { OptionProps } from 'ui/Select/Select.types';
import { FieldInputProps } from 'react-final-form';
import { useQuery } from '@tanstack/react-query';
import useUpdateEffect from './useUpdateEffect';
import { useEffectOnce } from './useEffectOnce';
import { useCurrentOrganizationId } from 'context/User';
import { NO_ORGANIZATION_KEY, USE_VIEW_TRANSITION } from 'helpers/constants';
import { useNotDesktop } from 'context/Breakpoint';

export const useSelect = <OptionValue>({
    input,
    value: fieldValue,
    options: rawOptions,
    onSearch,
    loader,
    getLabel,
    defaultOption,
    onChange,
    queryKey,
    staleTimeMs,
    followOrganization = true
}: {
    input?: FieldInputProps<OptionValue | OptionValue[], HTMLElement>;
    value?: OptionValue | OptionValue[];
    options?: OptionProps<OptionValue>[];
    onSearch?: (search: string) => Promise<OptionProps<OptionValue>[]>;
    loader?: () => Promise<OptionProps<OptionValue>[]>;
    getLabel?: (value: OptionValue) => string;
    defaultOption?: OptionProps<OptionValue>;
    keepSearch?: boolean;
    multiple?: boolean;
    onChange?: (
        value: OptionValue | OptionValue[],
        option?: OptionProps<OptionValue> | OptionProps<OptionValue>[]
    ) => void | Promise<void>;
    queryKey?: string[];
    staleTimeMs?: number;
    followOrganization?: boolean;
}) => {
    const organizationId = useCurrentOrganizationId();
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [options, setOptions] = useState(rawOptions);
    const [filteredOptions, setFilteredOptions] = useState<
        OptionProps<OptionValue>[]
    >(options || []);
    const [loading, setLoading] = useState(!!onSearch || !!loader);
    const transitionRef = useRef<any>();

    const value = useMemo(
        () => input?.value ?? fieldValue,
        [input, fieldValue]
    );

    const valueWithOption = useMemo(() => {
        if (value === null || value === undefined) {
            return defaultOption;
        }

        if (getLabel) {
            return Array.isArray(value)
                ? value.map((val) => ({ label: getLabel(val), value: val }))
                : { value, label: getLabel(value) };
        }

        const finalOptions =
            options && options.length > 0 ? options : filteredOptions;

        return Array.isArray(value)
            ? finalOptions.length > 0
                ? finalOptions.filter((opt) =>
                      value.some((val) => opt.value == val)
                  )
                : defaultOption
                  ? [defaultOption]
                  : undefined
            : finalOptions.find((opt) => opt.value == value) || defaultOption;
    }, [defaultOption, filteredOptions, getLabel, options, value]);

    const handleChange = useCallback(
        (
            value: OptionValue | OptionValue[],
            option?: OptionProps<OptionValue> | OptionProps<OptionValue>[]
        ) => {
            onChange?.(value, option);
            input?.onChange(value);
        },
        [input, onChange]
    );

    const mobile = useNotDesktop();

    const load = useCallback(async () => {
        let result;

        const getResult = async () => {
            if (onSearch) {
                result = await onSearch(searchValue);
                setFilteredOptions(result);
            } else if (loader) {
                result = await loader();
                setOptions(result);
            }
        };

        if (USE_VIEW_TRANSITION && !mobile) {
            transitionRef.current?.skipTransition();
            transitionRef.current = document.startViewTransition(getResult);
            await transitionRef.current.finished;
            return result;
        } else {
            await getResult();
            return result;
        }
    }, [onSearch, loader, searchValue, mobile]);

    const { data } = useQuery({
        queryKey: [
            'select',
            ...(queryKey ?? []),
            searchValue,
            followOrganization
                ? (organizationId ?? NO_ORGANIZATION_KEY)
                : NO_ORGANIZATION_KEY
        ],
        enabled: !!queryKey?.length,
        queryFn: load,
        staleTime: staleTimeMs
    });

    useUpdateEffect(() => {
        setOptions(rawOptions);
    }, [rawOptions]);

    const updateSearchValue = useCallback(
        (value: string) => {
            setSearchValue(value);
            load();
        },
        [load]
    );

    useEffectOnce(() => {
        load();
    });

    useEffect(() => {
        if (!onSearch) {
            setFilteredOptions(
                options?.filter(
                    (option) =>
                        String(option.label)
                            ?.toLowerCase()
                            .includes(searchValue.toLowerCase()) ||
                        String(option.value)
                            .toLowerCase()
                            .includes(searchValue.toLowerCase())
                ) || []
            );
        }
    }, [searchValue, options, onSearch]);

    return {
        isOpen,
        setIsOpen,
        searchValue,
        setSearchValue: updateSearchValue,
        options,
        filteredOptions: data ?? filteredOptions,
        loading,
        setLoading,
        value,
        valueWithOption,
        handleChange
    };
};
